import { CryptoError } from './errors.js'
import { toBufferSource } from './encoding.js'
import { randomBytes } from './random.js'

/**
 * AES-256-GCM via WebCrypto — PRD §7.4, §11.2.
 * No custom primitives: the profile is a versioned constant, nonces are
 * 96-bit CSPRNG output, AAD binds object identity/profile, decrypt fails closed.
 */

export interface AeadProfile {
  readonly id: string
  readonly algorithm: string
  readonly key_bits: number
  readonly nonce_bytes: number
  readonly tag_bits: number
}

export const AEAD_PROFILE_V1: AeadProfile = {
  id: 'signbento-aead-v1',
  algorithm: 'AES-GCM',
  key_bits: 256,
  nonce_bytes: 12,
  tag_bits: 128,
} as const

const AES_GCM_KEY_BYTES = AEAD_PROFILE_V1.key_bits / 8

export interface SealOptions {
  /** Only the profile's 128-bit tag is accepted; other lengths are rejected. */
  readonly tag_length?: number
}

export interface Aead {
  seal(
    plaintext: Uint8Array,
    aad: Uint8Array,
    key: CryptoKey,
    options?: SealOptions,
  ): Promise<Uint8Array>
  open(sealed: Uint8Array, aad: Uint8Array, key: CryptoKey): Promise<Uint8Array>
}

export async function generateAesGcmKey(): Promise<CryptoKey> {
  return globalThis.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: AEAD_PROFILE_V1.key_bits },
    true,
    ['encrypt', 'decrypt'],
  )
}

export async function importAesGcmKey(bytes: Uint8Array, extractable = false): Promise<CryptoKey> {
  if (bytes.byteLength !== AES_GCM_KEY_BYTES) {
    throw new CryptoError(
      'CRYPTO_INVALID_KEY',
      `AES-GCM-256 requires ${AES_GCM_KEY_BYTES} key bytes`,
    )
  }
  return globalThis.crypto.subtle.importKey(
    'raw',
    toBufferSource(bytes),
    { name: 'AES-GCM' },
    extractable,
    ['encrypt', 'decrypt'],
  )
}

export async function exportAesGcmKey(key: CryptoKey): Promise<Uint8Array> {
  try {
    return new Uint8Array(await globalThis.crypto.subtle.exportKey('raw', key))
  } catch {
    throw new CryptoError('CRYPTO_INVALID_KEY', 'key is not extractable')
  }
}

function assertKeyAlgorithm(key: CryptoKey): void {
  if (key.algorithm.name !== 'AES-GCM' || key.type !== 'secret') {
    throw new CryptoError('CRYPTO_INVALID_KEY', 'expected an AES-GCM secret key')
  }
}

export class WebCryptoAead implements Aead {
  /** Output layout: nonce (12 bytes) ‖ ciphertext ‖ 128-bit tag. */
  async seal(
    plaintext: Uint8Array,
    aad: Uint8Array,
    key: CryptoKey,
    options: SealOptions = {},
  ): Promise<Uint8Array> {
    assertKeyAlgorithm(key)
    const tagLength = options.tag_length ?? AEAD_PROFILE_V1.tag_bits
    if (tagLength !== AEAD_PROFILE_V1.tag_bits) {
      throw new CryptoError(
        'CRYPTO_TAG_LENGTH_UNSUPPORTED',
        `only ${AEAD_PROFILE_V1.tag_bits}-bit GCM tags are supported`,
      )
    }
    const nonce = randomBytes(AEAD_PROFILE_V1.nonce_bytes)
    const ciphertext = new Uint8Array(
      await globalThis.crypto.subtle.encrypt(
        {
          name: 'AES-GCM',
          iv: toBufferSource(nonce),
          tagLength,
          additionalData: toBufferSource(aad),
        },
        key,
        toBufferSource(plaintext),
      ),
    )
    const sealed = new Uint8Array(nonce.byteLength + ciphertext.byteLength)
    sealed.set(nonce, 0)
    sealed.set(ciphertext, nonce.byteLength)
    return sealed
  }

  async open(sealed: Uint8Array, aad: Uint8Array, key: CryptoKey): Promise<Uint8Array> {
    assertKeyAlgorithm(key)
    if (sealed.byteLength < AEAD_PROFILE_V1.nonce_bytes + AEAD_PROFILE_V1.tag_bits / 8) {
      throw new CryptoError('CRYPTO_AUTH_FAILED', 'sealed payload is too short')
    }
    const nonce = sealed.subarray(0, AEAD_PROFILE_V1.nonce_bytes)
    const ciphertext = sealed.subarray(AEAD_PROFILE_V1.nonce_bytes)
    try {
      return new Uint8Array(
        await globalThis.crypto.subtle.decrypt(
          {
            name: 'AES-GCM',
            iv: toBufferSource(nonce),
            tagLength: AEAD_PROFILE_V1.tag_bits,
            additionalData: toBufferSource(aad),
          },
          key,
          toBufferSource(ciphertext),
        ),
      )
    } catch {
      // Fail closed: wrong key, tampered ciphertext/tag or mismatched AAD.
      throw new CryptoError('CRYPTO_AUTH_FAILED', 'authentication failed for sealed payload')
    }
  }
}
